import {
  getHistoryIdentifier,
  isValidLatestReport,
  normalizeLatestReportState,
  normalizeReportRecord
} from './latestReportHelpers';

export const LATEST_REPORT_STORAGE_KEY = 'homehelper_latest_report_state';
export const LATEST_REPORT_EVENT = 'homehelper:latest-report-updated';

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function emitLatestReportUpdate() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(LATEST_REPORT_EVENT));
}

function writeState(nextState) {
  if (!canUseStorage()) {
    return normalizeLatestReportState(nextState);
  }

  const normalized = normalizeLatestReportState(nextState);

  try {
    window.localStorage.setItem(LATEST_REPORT_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    return normalized;
  }

  emitLatestReportUpdate();
  return normalized;
}

export function getLatestReportState() {
  const emptyState = normalizeLatestReportState();

  if (!canUseStorage()) {
    return emptyState;
  }

  let parsed;

  try {
    const raw = window.localStorage.getItem(LATEST_REPORT_STORAGE_KEY);
    if (!raw) {
      return emptyState;
    }
    parsed = JSON.parse(raw);
  } catch {
    return writeState(emptyState);
  }

  const normalized = normalizeLatestReportState(parsed);
  const shouldRepair = JSON.stringify(parsed) !== JSON.stringify(normalized);

  if (shouldRepair) {
    return writeState(normalized);
  }

  return normalized;
}

export function getLatestReport() {
  return getLatestReportState().latest;
}

export function getReportHistory() {
  return getLatestReportState().history;
}

export function saveLatestReport(report) {
  const normalizedReport = normalizeReportRecord(report);
  if (!normalizedReport) {
    return getLatestReportState();
  }

  const currentState = getLatestReportState();
  const history = [normalizedReport, ...currentState.history.filter((item) => {
    if (normalizedReport.projectGuid && item.projectGuid) {
      return item.projectGuid !== normalizedReport.projectGuid;
    }

    return item.reportUrl !== normalizedReport.reportUrl;
  })];

  return writeState({
    latest: normalizedReport,
    history
  });
}

export function clearLatestReport() {
  const currentState = getLatestReportState();
  return writeState({
    latest: null,
    history: currentState.history
  });
}

export function removeHistoryItem(projectGuidOrUrl) {
  const identifier = typeof projectGuidOrUrl === 'string' ? projectGuidOrUrl.trim() : '';
  if (!identifier) {
    return getLatestReportState();
  }

  const currentState = getLatestReportState();
  const history = currentState.history.filter((item) => {
    return item.projectGuid !== identifier && item.reportUrl !== identifier;
  });
  const latest = getHistoryIdentifier(currentState.latest) === identifier ? null : currentState.latest;

  return writeState({ latest, history });
}

export function clearLatestReportIfMatches(projectGuidOrUrl) {
  const identifier = typeof projectGuidOrUrl === 'string' ? projectGuidOrUrl.trim() : '';
  if (!identifier) {
    return getLatestReportState();
  }

  const currentState = getLatestReportState();
  const latestIdentifier = getHistoryIdentifier(currentState.latest);

  if (latestIdentifier !== identifier) {
    return currentState;
  }

  const nextLatest = currentState.history.find((item) => {
    return getHistoryIdentifier(item) !== identifier && isValidLatestReport(item);
  }) || null;
  const history = currentState.history.filter((item) => getHistoryIdentifier(item) !== identifier);

  return writeState({
    latest: nextLatest,
    history
  });
}